/* eslint-disable unicorn/filename-case -- TODO rename later */
import { ReactNode, useEffect, useState } from "react";
import Select, { GroupBase, Props, SingleValue } from "react-select";
import CreatableSelect from "react-select/creatable";

import { CaretDown, Search } from "~/assets/svg";
import { Button } from "~/components/__deprecated";
import { ThemeColors } from "~/themes";

type SelectProps<Option, isMulti extends boolean = true, Group extends GroupBase<Option> = GroupBase<Option>> = Props<
  Option,
  isMulti,
  Group
> & {
  creatable?: boolean;
  dataTestId?: string;
  defaultLabel?: string;
  hasCustomTrigger?: boolean;
  selectedValue?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  onSelect?: (value: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  isFixedHeight?: boolean;
};

const Blanket = (props: React.JSX.IntrinsicElements["div"]) => <div className="fixed inset-0 z-10" {...props} />;

const Menu = (props: React.JSX.IntrinsicElements["div"]) => (
  <div className="absolute z-50 rounded bg-white" {...props} />
);

const Dropdown = ({
  children,
  isOpen,
  target,
  onClose,
}: {
  children?: ReactNode;
  readonly isOpen: boolean;
  readonly target: ReactNode;
  readonly onClose: () => void;
}) => (
  <div className="relative">
    {target}
    {isOpen ? <Menu>{children}</Menu> : null}
    {isOpen ? <Blanket onClick={onClose} /> : null}
  </div>
);

const DropdownIndicator = () => (
  <div className="mr-[10px]">
    <Search color={ThemeColors.gray[400]} width="20px" height="20px" />
  </div>
);

/**
 * @deprecated Use `~/components/ui/select` instead.
 *
 * @example
 * import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select"
 */
const SelectMenu = <Option, isMulti extends boolean = true, Group extends GroupBase<Option> = GroupBase<Option>>(
  props: SelectProps<Option, isMulti, Group>,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    creatable,
    dataTestId,
    defaultLabel,
    defaultValue,
    hasCustomTrigger,
    selectedValue,
    onSelect,
    components,
    isFixedHeight = true,
  } = props;
  const [selectedOption, setSelectedOption] = useState<SingleValue<Option> | undefined>(selectedValue);

  useEffect(() => {
    if (selectedValue !== selectedOption) setSelectedOption(selectedValue);
  }, [selectedOption, selectedValue]);

  // Check if custom multiValue components are provided
  const hasCustomMultiValue = components?.MultiValueLabel || components?.MultiValueRemove;

  if (hasCustomTrigger) {
    return (
      <Dropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        target={
          <span className="flex w-72">
            <Button
              className="justify-between"
              dataTestId={dataTestId}
              endIcon={<CaretDown />}
              fullWidth
              label={selectedOption?.["label" as keyof typeof Option] || defaultLabel || "Select"}
              variant="ternary"
              onClick={() => setIsOpen((prev) => !prev)}
              small
            />
          </span>
        }
      >
        <Select
          menuPlacement="auto"
          placeholder="Search..."
          classNames={{
            control: () =>
              "!min-w-64 !border !border-gray-300 font-semibold text-sm text-gray-600 transition-colors !duration-150 rounded-t !rounded-b-none !shadow-none",
            container: () => "!w-72 !mt-1 !rounded-none",
            input: () => "!h-8 text-sm font-semibold text-gray-600 !rounded-b-none !border-b-0",
            placeholder: () => "!text-gray-500 text-sm !font-normal",
            menu: () =>
              "!mt-0 w-full !border !border-gray-300 rounded-b !rounded-t-none bg-white !shadow-md !border-t-0",
            option: ({ isSelected, isFocused }) =>
              `!py-2 !px-3 cursor-pointer !text-xs !font-semibold transition-colors !duration-150 ${
                isSelected
                  ? "!bg-primary-600 text-white"
                  : isFocused
                    ? "!bg-blue-50 text-gray-600"
                    : "bg-white text-gray-600 hover:!bg-blue-50"
              }`,
          }}
          defaultValue={selectedValue}
          {...props}
          onChange={(newValue) => {
            const opt = (newValue as SingleValue<Option>) || ({} as SingleValue<Option>);
            setSelectedOption(opt);
            setIsOpen(false);
            onSelect?.(opt);
          }}
          value={selectedOption?.["value" as keyof typeof Option]}
          backspaceRemovesValue={false}
          menuIsOpen
          components={{
            IndicatorSeparator: null,
            DropdownIndicator,
            ClearIndicator: undefined,
          }}
          controlShouldRenderValue={false}
        />
      </Dropdown>
    );
  }
  if (creatable) {
    return (
      <CreatableSelect
        formatCreateLabel={() => "Hint: Press enter to add"}
        classNames={{
          container: () => "w-full",
          control: () =>
            "!border !border-gray-300 rounded text-sm !shadow-[inset_0_1px_0_rgba(0,0,0,0.04)] hover:border-gray-400 focus-within:border-gray-400 transition-colors !duration-150 min-h-10",
          input: () => "!h-8 text-sm font-semibold text-gray-600",
          placeholder: () => "!text-gray-400 text-sm font-normal",
          multiValue: () => "overflow-hidden rounded-full bg-primary-300 h-7 m-1",
          multiValueLabel: () => "text-white text-sm font-semibold !px-2 !py-1",
          multiValueRemove: () =>
            "cursor-pointer text-white bg-primary-300 hover:text-gray-100 hover:bg-primary-700 transition-colors !duration-150 px-1",
        }}
        defaultValue={defaultValue}
        {...props}
      />
    );
  }
  return (
    <Select
      data-testid={dataTestId}
      defaultValue={defaultValue}
      classNames={{
        container: () => "w-full",
        control: () =>
          isFixedHeight
            ? "!border !border-border !border-[0.5px] !rounded-md font-semibold text-sm !shadow-[inset_0_1px_0_rgba(0,0,0,0.04)] hover:border-gray-400 focus-within:border-gray-400 transition-all !duration-150  !h-8 focus-within:ring-2 focus-within:ring-inset focus-within:ring-gray-100 overflow-visible"
            : "!border !border-gray-300 rounded font-semibold text-sm !shadow-[inset_0_1px_0_rgba(0,0,0,0.04)] hover:border-gray-400 focus-within:border-gray-400 transition-all !duration-150 min-h-10 focus-within:ring-2 focus-within:ring-inset focus-within:ring-gray-100 overflow-visible",
        input: () =>
          isFixedHeight ? "!h-6 text-sm font-semibold text-gray-600" : "!h-8 text-sm font-semibold text-gray-600",
        placeholder: () => "!text-gray-400 text-sm font-normal",
        singleValue: () => "text-gray-600 text-sm font-semibold",
        multiValue: () =>
          hasCustomMultiValue
            ? "h-7 overflow-hidden rounded-full m-1"
            : "h-7 overflow-hidden rounded-full !bg-primary-100 !border !border-primary-300 m-1",
        multiValueLabel: () => (hasCustomMultiValue ? "text-sm" : "text-white text-sm !py-[1px]"),
        multiValueRemove: () =>
          hasCustomMultiValue
            ? "cursor-pointer transition-colors !duration-150"
            : "cursor-pointer text-white hover:text-gray-100 transition-colors !duration-150 px-1 hover:bg-primary-700",
        menu: () => "shadow-xs rounded !border !border-gray-200 bg-white",
        option: ({ isSelected, isFocused }) =>
          `!py-2 !px-3 cursor-pointer text-sm transition-colors !duration-150 ${
            isSelected
              ? "!bg-primary-600 text-white"
              : isFocused
                ? "!bg-gray-100 text-gray-600"
                : "bg-white text-gray-600 hover:!bg-gray-100"
          }`,
      }}
      {...props}
    />
  );
};

export default SelectMenu;
